import { convexQuery } from '@convex-dev/react-query'
import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { FunctionReturnType } from 'convex/server'
import { IconArrowLeftOutline18, IconChatBotOutline18 } from 'nucleo-ui-outline-18'
import { useMemo, useState } from 'react'
import { Button } from '~/components/ui/button'
import { EmptyState } from '~/components/ui/empty-state'
import { PageHeader } from '~/components/ui/page-header'
import { Spinner } from '~/components/ui/spinner'
import { formatAbsoluteTime, formatDuration } from '~/lib/datetime'
import { titleFor } from '~/lib/route-title'
import { cn } from '~/lib/utils'
import { api } from '~convex/_generated/api'
import type { Id } from '~convex/_generated/dataModel'
import { formatMicroUsd } from '~convex/lib/aiCost'
import { TurnStatePill } from './index'

export const Route = createFileRoute('/admin/assistant/$turnId')({
  component: AdminTurnDetail,
  head: () => ({ meta: [{ title: titleFor(msg`Turn`) }] }),
})

type Detail = NonNullable<FunctionReturnType<typeof api.adminAssistant.turnDetail>>
type EventRow = Detail['events'][number]
type MessageRow = Detail['messages'][number]

const EVENT_LABEL: Record<EventRow['kind'], MessageDescriptor> = {
  agent_run: msg`Agent`,
  app_reply_rendered: msg`App reply rendered`,
  briefing_generated: msg`Daily briefing`,
  briefing_rendered: msg`First render`,
  classified: msg`Classifier`,
  digest_sent: msg`Reply`,
  hold_check_in: msg`Hold check-in`,
  hold_started: msg`Held`,
  media_stored: msg`Media`,
  reaction_sent: msg`Reaction`,
  reply_resolved: msg`Tap resolved`,
  searched: msg`Search`,
  summarized: msg`Summary`,
  superseded: msg`Superseded`,
  titled: msg`Title`,
  transcribed: msg`Transcription`,
  turn_closed: msg`Close attempt`,
  verdict_applied: msg`Verdict`,
  window_opened: msg`Window opened`,
}

/**
 * The two pipelines a media message runs through, each with its own outcome.
 * `pending` is the one worth noticing: a burst that closed while media was
 * still pending reached the agent without it.
 */
const MEDIA_STATE_LABEL: Record<
  NonNullable<MessageRow['mediaState']>,
  { className?: string; label: MessageDescriptor }
> = {
  failed: { className: 'text-red-600 dark:text-red-400', label: msg`media lost` },
  pending: { className: 'text-amber-600 dark:text-amber-400', label: msg`media pending` },
  skipped: { label: msg`media skipped` },
  stored: { label: msg`filed` },
}

const TRANSCRIPT_STATE_LABEL: Record<
  NonNullable<MessageRow['transcriptState']>,
  { className?: string; label: MessageDescriptor }
> = {
  done: { label: msg`transcribed` },
  failed: { className: 'text-red-600 dark:text-red-400', label: msg`transcription failed` },
  pending: { className: 'text-amber-600 dark:text-amber-400', label: msg`transcription pending` },
  skipped: { label: msg`transcription skipped` },
}

const KIND_LABEL: Record<MessageRow['kind'], MessageDescriptor> = {
  audio: msg`Audio`,
  contacts: msg`Contact card`,
  document: msg`Document`,
  image: msg`Image`,
  interactive: msg`Button reply`,
  location: msg`Location`,
  reaction: msg`Reaction`,
  sticker: msg`Sticker`,
  text: msg`Text`,
  unsupported: msg`Unsupported`,
  video: msg`Video`,
}

/**
 * One merged stream: what the user sent and what the engine decided, in the
 * order it happened. They are stored apart — messages carry Meta's send time,
 * events carry our recording time — and reconciling them is exactly what
 * makes a delayed webhook or a raced timer legible.
 */
type TimelineEntry =
  | { at: number; event: EventRow; type: 'event' }
  | { at: number; message: MessageRow; type: 'message' }

const buildTimeline = (detail: Detail): TimelineEntry[] =>
  [
    ...detail.events.map((event): TimelineEntry => ({ at: event.at, event, type: 'event' })),
    ...detail.messages.map(
      (message): TimelineEntry => ({ at: message.timestamp, message, type: 'message' })
    ),
  ].sort((a, b) => a.at - b.at)

function AdminTurnDetail() {
  const { t } = useLingui()
  const { turnId } = Route.useParams()
  // A URL segment is an untyped string until the query proves otherwise; a
  // bad id reads as "not found" below rather than throwing.
  const { data } = useQuery(
    convexQuery(api.adminAssistant.turnDetail, { turnId: turnId as Id<'turns'> })
  )

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        meta={
          data === undefined || data === null
            ? undefined
            : t`${data.turn.userName ?? t`Unclaimed`} · ${data.turn.phoneE164 ?? '—'}`
        }
        title={t`Turn`}
      />
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 pb-6">
        <Button render={<Link to="/admin/assistant" />} size="sm" variant="ghost">
          <IconArrowLeftOutline18 />
          <Trans>All turns</Trans>
        </Button>

        {data === undefined ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : data === null ? (
          <EmptyState
            description={t`This turn no longer exists.`}
            icon={IconChatBotOutline18}
            title={t`Turn not found`}
          />
        ) : (
          <>
            <SummaryCard detail={data} />
            <ConversationSummary detail={data} />
            <ToolCalls detail={data} />
            <Timeline detail={data} />
          </>
        )}
      </div>
    </div>
  )
}

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-muted-foreground text-xs">{label}</span>
    <span className="font-medium text-sm tabular-nums">{value}</span>
  </div>
)

const SummaryCard = ({ detail }: { detail: Detail }) => {
  const { t, i18n } = useLingui()
  const { turn } = detail
  const failedCalls = detail.toolCalls.filter((action) => action.status === 'failed').length

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-3 pb-4">
        <TurnStatePill state={turn.state} />
        {turn.verdict === null ? null : (
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-muted-foreground text-xs">
            {turn.verdict}
          </span>
        )}
        <span className="text-muted-foreground text-sm">
          {turn.workspaceName ?? t`No workspace`}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label={t`Started`} value={formatAbsoluteTime(turn.createdAt, i18n)} />
        <Stat
          label={t`Elapsed`}
          value={turn.elapsedMs === null ? '—' : formatDuration(turn.elapsedMs)}
        />
        <Stat label={t`Generation`} value={String(turn.generation)} />
        <Stat label={t`Messages`} value={String(detail.messages.length)} />
        <Stat
          label={t`AI calls`}
          value={
            detail.aiCalls === 0
              ? '—'
              : `${detail.aiCalls} · ${formatDuration(detail.modelMs)} · ${formatMicroUsd(detail.costMicroUsd)}`
          }
        />
        <Stat
          label={t`Failed steps`}
          value={detail.errorCount === 0 ? t`None` : String(detail.errorCount)}
        />
        <Stat
          label={t`Actions`}
          value={
            detail.toolCalls.length === 0
              ? '—'
              : failedCalls === 0
                ? String(detail.toolCalls.length)
                : t`${detail.toolCalls.length} · ${failedCalls} failed`
          }
        />
        {/* The prompt-caching health check, one glance wide: a turn that read
            nothing from cache after the first burst means a frozen layer
            stopped being frozen. */}
        <Stat
          label={t`Prompt cache`}
          value={
            detail.cacheReadTokens === 0 && detail.cacheWriteTokens === 0
              ? '—'
              : t`${detail.cacheReadTokens} read · ${detail.cacheWriteTokens} written`
          }
        />
      </div>
      {turn.closesAt === null ? null : (
        <p className="pt-3 text-muted-foreground text-xs">
          <Trans>Scheduled to close at {formatAbsoluteTime(turn.closesAt, i18n)}</Trans>
        </p>
      )}
    </section>
  )
}

/**
 * The rolling summary as it stands now (#72) — prompt layer 3a, and the one
 * agent input that cannot be reconstructed from the messages on this page.
 *
 * Rendered whole rather than truncated: it is capped at ~400 tokens by
 * construction, and a summary drifting off what the chat is actually about is
 * only visible if you can read the whole thing.
 */
const ConversationSummary = ({ detail }: { detail: Detail }) => {
  const { i18n } = useLingui()
  const { text, through } = detail.conversationSummary

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2 pb-2">
        <h2 className="font-medium text-sm">
          <Trans>Conversation summary</Trans>
        </h2>
        {through === null ? null : (
          <span className="text-muted-foreground text-xs tabular-nums">
            <Trans>through the turn of {formatAbsoluteTime(through, i18n)}</Trans>
          </span>
        )}
      </div>
      {text === null ? (
        <p className="text-muted-foreground text-sm">
          <Trans>Nothing summarised yet. The agent is reading raw history alone.</Trans>
        </p>
      ) : (
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{text}</p>
      )}
    </section>
  )
}

/**
 * A parked call reads as amber rather than red: nothing failed, the assistant
 * is simply waiting for the user to tap an answer.
 */
const ACTION_STATUS_LABEL: Record<Detail['toolCalls'][number]['status'], MessageDescriptor> = {
  executed: msg`executed`,
  executing: msg`executing`,
  failed: msg`failed`,
  pending_confirm: msg`waiting on a tap`,
  undone: msg`undone`,
}

/**
 * What the agent did to the workspace, in call order.
 *
 * Rendered as its own block rather than folded into the timeline because it
 * answers a different question: the timeline says what the machine did, this
 * says what CHANGED. A digest claiming a task was created next to an empty
 * action list is the failure this section exists to make visible.
 */
const ToolCalls = ({ detail }: { detail: Detail }) => {
  const { i18n } = useLingui()
  if (detail.toolCalls.length === 0) {
    return null
  }
  return (
    <section>
      <h2 className="pb-3 font-medium text-muted-foreground text-sm">
        <Trans>Actions</Trans>
      </h2>
      <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
        {detail.toolCalls.map((action) => (
          <li className="flex items-baseline gap-3 px-4 py-2.5 text-sm" key={action._id}>
            <span className="w-6 shrink-0 text-muted-foreground text-xs tabular-nums">
              {action.seq + 1}
            </span>
            <span className="shrink-0 font-medium font-mono text-xs">{action.tool}</span>
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {action.argsSummary}
            </span>
            <span
              className={cn(
                'shrink-0 rounded-full px-1.5 py-0.5 text-xs',
                action.status === 'failed'
                  ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                  : action.status === 'pending_confirm'
                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                    : 'bg-muted text-muted-foreground'
              )}
            >
              {i18n._(ACTION_STATUS_LABEL[action.status])}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * What one paid step consumed. Only steps that called a model have a `model`,
 * so this renders nothing for the timers and reactions that make up most of
 * the timeline.
 */
const UsageLine = ({ event }: { event: EventRow }) => {
  if (event.model === null) {
    return null
  }
  const parts = [event.model]
  if (event.inputTokens !== null) {
    parts.push(`${event.inputTokens}→${event.outputTokens ?? 0} tok`)
  }
  // `inputTokens` counts only what was NOT cached, so the two read together:
  // a small input beside a large cache read is the prompt layering working.
  if (event.cacheReadTokens) {
    parts.push(`${event.cacheReadTokens} cache↓`)
  }
  if (event.cacheWriteTokens) {
    parts.push(`${event.cacheWriteTokens} cache↑`)
  }
  // Only the duration-billed transcription models report this; the
  // token-billed ones charge by audio token instead.
  if (event.audioSeconds !== null) {
    parts.push(`${event.audioSeconds.toFixed(1)}s audio`)
  }
  if (event.costMicroUsd !== null) {
    parts.push(formatMicroUsd(event.costMicroUsd))
  }
  return <p className="pt-0.5 text-muted-foreground text-xs tabular-nums">{parts.join(' · ')}</p>
}

const EventLine = ({
  event,
  isHighlighted,
  offsetMs,
  onTraceBack,
  setBy,
}: {
  event: EventRow
  isHighlighted: boolean
  offsetMs: number
  onTraceBack: (eventId: string) => void
  /** The step that set this one's timer, when a timer is what woke it. */
  setBy: { label: string; offsetMs: number } | undefined
}) => {
  const { i18n } = useLingui()

  return (
    <li
      className={cn(
        'flex gap-3 px-4 py-2.5 text-sm transition-colors',
        isHighlighted && 'bg-amber-500/10'
      )}
      id={event._id}
    >
      <span className="w-16 shrink-0 text-muted-foreground text-xs tabular-nums">
        +{formatDuration(offsetMs)}
      </span>
      <span
        className={cn(
          'w-28 shrink-0 text-xs',
          event.ok ? 'text-muted-foreground' : 'font-medium text-red-600 dark:text-red-400'
        )}
      >
        {i18n._(EVENT_LABEL[event.kind])}
      </span>
      <div className="min-w-0 flex-1">
        <p className="break-words">
          {event.detail ?? <span className="text-muted-foreground">—</span>}
        </p>
        {event.errorMessage === null ? null : (
          <p className="break-words pt-0.5 font-mono text-red-600 text-xs dark:text-red-400">
            {event.errorMessage}
          </p>
        )}
        <UsageLine event={event} />
        {setBy === undefined || event.scheduledByEventId === null ? null : (
          // Several timers are in flight at once and most step aside, so the
          // useful question is which alarm this was — not just that one rang.
          <button
            className="pt-0.5 text-muted-foreground text-xs underline decoration-dotted underline-offset-2 hover:text-foreground"
            onClick={() => onTraceBack(event.scheduledByEventId ?? '')}
            type="button"
          >
            <Trans>
              alarm set at +{setBy.offsetMs === 0 ? '0ms' : formatDuration(setBy.offsetMs)} by{' '}
              {setBy.label}
            </Trans>
          </button>
        )}
      </div>
      <span className="w-16 shrink-0 text-right text-muted-foreground text-xs tabular-nums">
        {event.durationMs === null ? '' : formatDuration(event.durationMs)}
      </span>
      <span className="w-20 shrink-0 text-right text-muted-foreground text-xs">
        {event.outcome ?? ''}
      </span>
    </li>
  )
}

const MessageLine = ({ message, offsetMs }: { message: MessageRow; offsetMs: number }) => {
  const { i18n } = useLingui()

  return (
    <li
      className={cn(
        'flex gap-3 px-4 py-2.5 text-sm',
        message.direction === 'inbound' ? 'bg-accent/30' : 'bg-transparent'
      )}
    >
      <span className="w-16 shrink-0 text-muted-foreground text-xs tabular-nums">
        +{formatDuration(offsetMs)}
      </span>
      <span className="w-28 shrink-0 text-xs">
        {message.direction === 'inbound' ? <Trans>Message in</Trans> : <Trans>Message out</Trans>}
      </span>
      <div className="min-w-0 flex-1">
        <p className="break-words">
          {message.text ?? message.transcript ?? (
            <span className="text-muted-foreground">{i18n._(KIND_LABEL[message.kind])}</span>
          )}
        </p>
        <p className="flex flex-wrap gap-2 pt-0.5 text-muted-foreground text-xs">
          <span>{i18n._(KIND_LABEL[message.kind])}</span>
          {message.isVoiceNote ? (
            <span>
              <Trans>voice note</Trans>
            </span>
          ) : null}
          {message.mediaMimeType === null ? null : (
            <span className="font-mono">{message.mediaMimeType}</span>
          )}
          {message.mediaState === null ? null : (
            <span className={MEDIA_STATE_LABEL[message.mediaState].className}>
              {i18n._(MEDIA_STATE_LABEL[message.mediaState].label)}
            </span>
          )}
          {message.transcriptState === null ? null : (
            <span className={TRANSCRIPT_STATE_LABEL[message.transcriptState].className}>
              {i18n._(TRANSCRIPT_STATE_LABEL[message.transcriptState].label)}
            </span>
          )}
          {message.forwarded ? (
            <span className="italic">
              <Trans>forwarded</Trans>
            </span>
          ) : null}
          {message.errorMessage === null ? null : (
            <span className="text-red-600 dark:text-red-400">{message.errorMessage}</span>
          )}
          {/* Billing only exists on what we sent, and today it should always
              read free — a "billable" here means something left the 24h window. */}
          {message.billable === null ? null : (
            <span
              className={cn(message.billable && 'font-medium text-amber-600 dark:text-amber-400')}
            >
              {message.billable ? (
                <Trans>billable · {message.pricingCategory ?? '—'}</Trans>
              ) : (
                <Trans>free · {message.pricingCategory ?? '—'}</Trans>
              )}
            </span>
          )}
          {message.excludedFromTurn ? (
            // The two rows that are logged but never reach the agent: flush
            // gestures, and the phantom row Meta emits before every album.
            <span className="rounded bg-muted px-1">
              <Trans>excluded from turn</Trans>
            </span>
          ) : null}
        </p>
      </div>
    </li>
  )
}

const Timeline = ({ detail }: { detail: Detail }) => {
  const { i18n } = useLingui()
  const [highlighted, setHighlighted] = useState<string | undefined>(undefined)
  const timeline = buildTimeline(detail)
  const start = detail.turn.createdAt

  // Where each event sits, so a timer can name the step that set it by its
  // offset — "+3.1s Window opened" locates it in the list far better than an id.
  const byId = useMemo(() => {
    const map = new Map<string, { label: string; offsetMs: number }>()
    for (const event of detail.events) {
      map.set(event._id, {
        label: i18n._(EVENT_LABEL[event.kind]),
        offsetMs: Math.max(0, event.at - start),
      })
    }
    return map
  }, [detail.events, i18n, start])

  const traceBack = (eventId: string) => {
    setHighlighted(eventId)
    document.getElementById(eventId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  return (
    <section>
      <h2 className="pb-3 font-medium text-muted-foreground text-sm">
        <Trans>Timeline</Trans>
      </h2>
      {timeline.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          <Trans>Nothing was recorded against this turn.</Trans>
        </p>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {timeline.map((entry) =>
            entry.type === 'event' ? (
              <EventLine
                event={entry.event}
                isHighlighted={highlighted === entry.event._id}
                key={entry.event._id}
                offsetMs={Math.max(0, entry.at - start)}
                onTraceBack={traceBack}
                setBy={
                  entry.event.scheduledByEventId === null
                    ? undefined
                    : byId.get(entry.event.scheduledByEventId)
                }
              />
            ) : (
              <MessageLine
                key={entry.message._id}
                message={entry.message}
                offsetMs={Math.max(0, entry.at - start)}
              />
            )
          )}
        </ul>
      )}
      {detail.eventsCapped ? (
        <p className="pt-3 text-muted-foreground text-xs">
          <Trans>Only the first recorded steps are shown because this turn hit the read cap.</Trans>
        </p>
      ) : null}
    </section>
  )
}
